import React, {useEffect, useMemo, useState} from 'react';
import classes from './cabinet.module.css';
import {
    downloadWebConfig,
    createWebConfig,
    createPremiumWebConfig,
    deleteWebConfig,
    getServers,
    getWebConfigs,
    getWebProfile,
    getVlessCountries,
    getVpnSubscription,
    ensureVpnSubscription,
    rotateVpnSubscription,
} from '../../../utils/api';
import {useNavigate} from 'react-router-dom';
import TopupModal from '../../common/topup-modal/topup-modal';
import nid from "../app/servers/nid.png";
import itali from "../app/servers/itali.png";
import polsh from "../app/servers/polsh.png";
import kaz from "../app/servers/kaz.png";
import latvia from "../app/servers/latvia.png";
import tur from "../app/servers/tur.png";
import rus from "../app/servers/rus.png";
import usa from "../app/servers/usa.png";
import japan from "../app/servers/japan.png";
import spain from "../app/servers/spain.png";
import france from "../app/servers/france.png";
import germany from "../app/servers/germany.png";
import wireguard from "../app/servers/wireguard.png";
import amneziaWG from "../app/servers/amneziaWG.png";
import vless from "../app/servers/vless.png";

function getCountryIcon(country) {
    switch (country) {
        case 'Нидерланды':
            return nid;
        case 'Италия':
            return itali;
        case 'Польша':
            return polsh;
        case 'Казахстан':
            return kaz;
        case 'Латвия':
            return latvia;
        case 'Турция':
            return tur;
        case 'Россия':
            return rus;
        case 'США':
            return usa;
        case 'Япония':
            return japan;
        case 'Испания':
            return spain;
        case 'Франция':
            return france;
        case 'Германия':
            return germany;
        default:
            return null;
    }
}

function getVlessInbound(clientType) {
    const c = String(clientType || '').trim();
    if (!c.toLowerCase().startsWith('vless')) return null;
    const parts = c.split(' ').filter(Boolean);
    return parts.length >= 2 ? parts[1] : null;
}

function getProtocolIconSrc(protocol) {
    if (protocol === 'Wireguard') return wireguard;
    if (protocol === 'AmneziaWG') return amneziaWG;
    if (protocol === 'VLESS') return vless;
    return null;
}

function getProtocolAndSubprotocol(clientType) {
    const c = String(clientType || '').trim();
    if (c.toLowerCase().startsWith('vless')) {
        const inbound = getVlessInbound(c);
        return { protocol: 'VLESS', subprotocol: inbound || '—' };
    }
    if (c === 'Wireguard') return { protocol: 'Wireguard', subprotocol: '' };
    if (c === 'AmneziaWG') return { protocol: 'AmneziaWG', subprotocol: '' };
    return { protocol: c || '—', subprotocol: '—' };
}

const Cabinet = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [configs, setConfigs] = useState([]);
    const [servers, setServers] = useState([]);
    const [profile, setProfile] = useState(null);
    const [notice, setNotice] = useState(null);

    const [addOpen, setAddOpen] = useState(false);
    const [protocol, setProtocol] = useState('Wireguard');
    const [creating, setCreating] = useState(false);
    const [premiumConfirmOpen, setPremiumConfirmOpen] = useState(false);
    const [premiumConfirmOpt, setPremiumConfirmOpt] = useState(null);
    const [topupOpen, setTopupOpen] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const [qrImage, setQrImage] = useState('');
    const [qrTitle, setQrTitle] = useState('');
    const [vlessCountries, setVlessCountries] = useState([]);
    const [vpnSub, setVpnSub] = useState({ exists: false, url: null, enabled: false });
    const [subBusy, setSubBusy] = useState(false);

    const webToken = typeof window !== 'undefined' ? window.localStorage.getItem('efv_web_token') : null;
    useEffect(() => {
        if (!webToken) {
            navigate('/auth');
            return;
        }

        const load = async () => {
            setLoading(true);
            try {
                const [cfg, serversRes, profileRes, vlessOpts, sub] = await Promise.all([
                    getWebConfigs(),
                    getServers(),
                    getWebProfile(),
                    getVlessCountries(),
                    getVpnSubscription(),
                ]);
                setConfigs(cfg);
                setServers(serversRes?.res || []);
                setProfile(profileRes);
                setVlessCountries(Array.isArray(vlessOpts) ? vlessOpts : []);
                setVpnSub(sub || { exists: false, url: null, enabled: false });
            } catch (err) {
                const msg = err?.message || 'Не удалось загрузить данные';
                setNotice({variant: 'error', text: msg});
            } finally {
                setLoading(false);
            }
        };

        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const vlessLocationNames = useMemo(() => {
        return (vlessCountries || [])
            .map((x) => x.country || x.name)
            .filter(Boolean);
    }, [vlessCountries]);

    const wireguardOptions = useMemo(() => {
        const wgServers = servers.filter((s) => s.client === 'Wireguard');
        const map = new Map();
        for (const s of wgServers) {
            const key = `${s.country}__Wireguard__${s.is_premium ? 1 : 0}`;
            const online = Boolean(s.online);
            if (!map.has(key)) {
                map.set(key, {
                    name: s.country,
                    client: s.client,
                    online,
                    is_premium: Boolean(s.is_premium),
                });
            } else {
                const prev = map.get(key);
                map.set(key, { ...prev, online: Boolean(prev?.online) || online });
            }
        }
        return [...map.values()]
            .sort((a, b) => (a.is_premium === b.is_premium ? 0 : a.is_premium ? -1 : 1))
            .map((x, idx) => ({id: idx + 1, ...x}));
    }, [servers]);

    const amneziaOptions = useMemo(() => {
        const aServers = servers.filter((s) => s.client === 'AmneziaWG');
        const map = new Map();
        for (const s of aServers) {
            const key = `${s.country}__AmneziaWG__${s.is_premium ? 1 : 0}`;
            const online = Boolean(s.online);
            if (!map.has(key)) {
                map.set(key, {
                    name: s.country,
                    client: s.client,
                    online,
                    is_premium: Boolean(s.is_premium),
                });
            } else {
                const prev = map.get(key);
                map.set(key, { ...prev, online: Boolean(prev?.online) || online });
            }
        }
        return [...map.values()]
            .sort((a, b) => (a.is_premium === b.is_premium ? 0 : a.is_premium ? -1 : 1))
            .map((x, idx) => ({id: idx + 1, ...x}));
    }, [servers]);

    const currentOptions = useMemo(() => {
        if (protocol === 'Wireguard') return wireguardOptions;
        return amneziaOptions;
    }, [protocol, wireguardOptions, amneziaOptions]);

    const tunnelConfigs = useMemo(
        () => (configs || []).filter((c) => c.kind !== 'vless_profile' && !c.profile_id),
        [configs]
    );

    const displayName = profile?.telegram_name || profile?.site_login || 'Пользователь';
    const avatar = profile?.telegram_avatar || '';
    const tariffDay = Number.isFinite(Number(profile?.tariff_price_per_day))
        ? Number(profile?.tariff_price_per_day)
        : null;
    const tariffMonth = Number.isFinite(Number(profile?.tariff_price_per_month))
        ? Number(profile?.tariff_price_per_month)
        : null;
    const tariffPriceText =
        tariffDay !== null && tariffMonth !== null ? `${tariffDay}₽/сут, ${tariffMonth}₽/мес` : '';
    const profileInitial = String(displayName || 'U').trim().charAt(0).toUpperCase() || 'U';
    const telegramDisplayName = profile?.telegram_name || profile?.telegram_login || '';
    const telegramDisplayId = profile?.telegram_id ? `id${profile.telegram_id}` : '';
    const isVipUser =
        profile?.premium === true || String(profile?.tariff_name || '').toUpperCase() === 'VIP';

    async function refresh() {
        const [cfg, vlessOpts, sub] = await Promise.all([
            getWebConfigs(),
            getVlessCountries(),
            getVpnSubscription(),
        ]);
        setConfigs(cfg);
        setVlessCountries(Array.isArray(vlessOpts) ? vlessOpts : []);
        setVpnSub(sub || { exists: false, url: null, enabled: false });
    }

    async function refreshProfileOnly() {
        try {
            const profileRes = await getWebProfile();
            setProfile(profileRes);
        } catch {
            /* ignore */
        }
    }

    async function onCreate(opt) {
        if (!opt) return;
        if (opt.is_premium) {
            if (isVipUser) {
                setNotice({variant: 'error', text: 'Premium-серверы недоступны для вашего тарифа'});
                return;
            }
            setPremiumConfirmOpt(opt);
            setPremiumConfirmOpen(true);
            return;
        }

        setCreating(true);
        setNotice(null);
        try {
            await createWebConfig({country: opt.name, platform: opt.client});
            setAddOpen(false);
            await refresh();
            let freshProfile = profile;
            try {
                freshProfile = await getWebProfile();
                setProfile(freshProfile);
            } catch {
                /* оставляем старый profile */
            }
            const vipAfter =
                freshProfile?.premium === true ||
                String(freshProfile?.tariff_name || '').toUpperCase() === 'VIP';
            setNotice({
                variant: 'success',
                text: 'VPN-сервер создан.',
            });
            void vipAfter;
        } catch (err) {
            setNotice({variant: 'error', text: err?.message || 'Не удалось создать VPN-сервер'});
        } finally {
            setCreating(false);
        }
    }

    async function confirmPremiumCreate() {
        if (!premiumConfirmOpt) return;
        const opt = premiumConfirmOpt;
        setPremiumConfirmOpen(false);
        setPremiumConfirmOpt(null);

        setCreating(true);
        setNotice(null);
        try {
            await createPremiumWebConfig({country: opt.name, platform: opt.client});
            setAddOpen(false);
            await refresh();

            let freshProfile = profile;
            try {
                freshProfile = await getWebProfile();
                setProfile(freshProfile);
            } catch {
                /* оставляем старый profile */
            }
            setNotice({
                variant: 'success',
                text: 'VPN-сервер создан.',
            });
            void freshProfile;
        } catch (err) {
            setNotice({variant: 'error', text: err?.message || 'Не удалось создать VPN-сервер'});
        } finally {
            setCreating(false);
        }
    }

    async function onDelete(configItem) {
        setNotice(null);
        try {
            await deleteWebConfig({config: typeof configItem === 'string' ? configItem : configItem.name});
            await refresh();
            setNotice({variant: 'success', text: 'VPN-сервер удалён.'});
        } catch (err) {
            setNotice({variant: 'error', text: err?.message || 'Не удалось удалить VPN-сервер'});
        }
    }

    async function onEnsureSubscription() {
        setSubBusy(true);
        setNotice(null);
        try {
            const sub = await ensureVpnSubscription();
            setVpnSub(sub || { exists: true, url: sub?.url, enabled: true });
            setNotice({ variant: 'success', text: 'Подписка готова. Добавьте ссылку в v2rayTun.' });
        } catch (err) {
            setNotice({ variant: 'error', text: err?.message || 'Не удалось создать подписку' });
        } finally {
            setSubBusy(false);
        }
    }

    async function onCopySubscription() {
        const url = vpnSub?.url;
        if (!url) {
            await onEnsureSubscription();
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            setNotice({ variant: 'success', text: 'Ссылка подписки скопирована.' });
        } catch {
            setNotice({ variant: 'error', text: 'Не удалось скопировать ссылку' });
        }
    }

    async function onRotateSubscription() {
        setSubBusy(true);
        try {
            const sub = await rotateVpnSubscription();
            setVpnSub(sub || { exists: true, url: sub?.url, enabled: true });
            setNotice({ variant: 'success', text: 'Ссылка подписки обновлена. Обновите её в v2rayTun.' });
        } catch (err) {
            setNotice({ variant: 'error', text: err?.message || 'Не удалось обновить ссылку' });
        } finally {
            setSubBusy(false);
        }
    }

    function onOpenQr(configItem) {
        const base64 = String(configItem?.qr_code_base64 || '').trim();
        if (!base64) {
            setNotice({variant: 'error', text: 'QR-код пока недоступен для этого VPN-сервера'});
            return;
        }
        setQrImage(`data:image/png;base64,${base64}`);
        setQrTitle(configItem?.name ? String(configItem.name).replace('awg_', '') : 'QR-код');
        setQrOpen(true);
    }

    return (
        <div className={classes.page}>
            <div className={classes.heroMax}>
                <header className={classes.topBar}>
                    <div className={classes.headerLeft}>
                        <div className={classes.avatarWrap} title={displayName}>
                            {avatar ? (
                                <img className={classes.userAvatar} src={avatar} alt="" />
                            ) : (
                                <div className={classes.userAvatarFallback}>{profileInitial}</div>
                            )}
                        </div>

                        <div className={classes.userInfo}>
                            <div className={classes.userLogin}>{profile?.site_login || '—'}</div>
                            {telegramDisplayName ? (
                                <div className={classes.userTelegramName}>{telegramDisplayName}</div>
                            ) : null}
                            {telegramDisplayId ? (
                                <div className={classes.userTelegramId}>{telegramDisplayId}</div>
                            ) : null}
                        </div>

                        <div className={classes.profileMeta}>
                            <div className={classes.profileActions}>
                                <button
                                    type="button"
                                    className={classes.profileBtn}
                                    onClick={() => {
                                        navigate('/profile');
                                    }}
                                >
                                    Профиль
                                </button>
                                <button type="button" className={classes.profileBtn} onClick={() => navigate('/help')}>
                                    Инструкция
                                </button>
                                <button type="button" className={classes.profileBtn} onClick={() => navigate('/apps')}>
                                    Приложение
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerRight}>
                        <div className={classes.balanceText}>Баланс: {profile?.balance ?? 0}₽</div>
                        <div className={classes.tariffText}>
                            Тариф: {tariffPriceText ? `${tariffPriceText}` : ''}
                        </div>
                        <button
                            type="button"
                            className={classes.topupBtn}
                            onClick={() => setTopupOpen(true)}
                        >
                            Пополнить баланс
                        </button>
                    </div>
                </header>
            </div>

            <main className={classes.main}>
                <div className={classes.card}>
                    <div className={classes.headerRow}>
                        <h1 className={classes.cardTitle}>Личный кабинет</h1>
                        <button type="button" className={classes.addBtn} onClick={() => setAddOpen(true)}>
                            Добавить VPN-сервер
                        </button>
                    </div>

                    {notice ? (
                        <div
                            className={
                                notice.variant === 'success'
                                    ? classes.noticeSuccess
                                    : notice.variant === 'info'
                                        ? classes.noticeInfo
                                        : classes.noticeError
                            }
                            role="alert"
                        >
                            {notice.text}
                        </div>
                    ) : null}

                    {loading ? (
                        <div className={classes.loading}>Загрузка...</div>
                    ) : (
                        <>
                            <div className={classes.subCard}>
                                <div className={classes.subCardBody}>
                                    <div className={classes.subCardMain}>
                                        <div className={classes.subIconWrap} aria-hidden="true">
                                            <img className={classes.subIcon} src={vless} alt="" />
                                        </div>
                                        <div className={classes.subCardText}>
                                            <div className={classes.subCardTitle}>VLESS-подписка</div>
                                            <div className={classes.subCardMeta}>
                                                {vpnSub?.exists
                                                    ? 'Одна ссылка · РФ напрямую · зарубежье через VPN'
                                                    : 'Получите постоянную ссылку подписки'}
                                            </div>
                                            {vlessLocationNames.length ? (
                                                <div className={classes.subLocations}>
                                                    {vlessLocationNames.map((name) => {
                                                        const flag = getCountryIcon(name);
                                                        return (
                                                            <span className={classes.subLocChip} key={name}>
                                                                {flag ? (
                                                                    <img className={classes.subLocFlag} src={flag} alt="" />
                                                                ) : null}
                                                                {name}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className={classes.subCardActions}>
                                        {!vpnSub?.exists ? (
                                            <button
                                                type="button"
                                                className={classes.subBtn}
                                                disabled={subBusy}
                                                title="VLESS"
                                                onClick={onEnsureSubscription}
                                            >
                                                VLESS
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className={classes.subBtn}
                                                    disabled={subBusy}
                                                    title="Скопировать ссылку"
                                                    onClick={onCopySubscription}
                                                >
                                                    Скопировать
                                                </button>
                                                <button
                                                    type="button"
                                                    className={classes.subBtnSecondary}
                                                    disabled={subBusy}
                                                    title="Обновить ссылку"
                                                    onClick={onRotateSubscription}
                                                >
                                                    Rotate
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={classes.configList}>
                            {tunnelConfigs.length ? (
                                tunnelConfigs.map((c) => {
                                    const isVlessProfile = false;
                                    return (
                                    <div key={c.name} className={classes.configCard}>
                                        <div className={classes.configMain}>
                                            <div className={classes.configMain2}>
                                                <div className={classes.configCardRow}>
                                                    <div className={classes.configCardLeft}>
                                                        {(() => {
                                                            const flag = getCountryIcon(c.server?.country);
                                                            return flag ? (
                                                                <img className={classes.serverCountryIcon} src={flag} alt="" />
                                                            ) : null;
                                                        })()}
                                                        <div className={classes.serverCountryColumn}>
                                                            <div className={classes.serverCountry}>{c.server?.country}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={classes.configMetaCompact}>
                                                    <div className={classes.metaItem}>
                                                        {c.server?.is_premium ? (
                                                            <span className={classes.premiumSmall}>Premium</span>
                                                        ) : null}{' '}
                                                        {isVlessProfile
                                                            ? 'Локация · через подписку'
                                                            : `ID: ${String(c.name).replace('awg_', '')}`}
                                                    </div>
                                                    <div className={`${classes.metaItem} ${classes.metaProtocolItem}`}>
                                                        {getProtocolIconSrc(getProtocolAndSubprotocol(c.server?.client).protocol) ? (
                                                            <img
                                                                className={classes.protocolIconSmall}
                                                                src={getProtocolIconSrc(getProtocolAndSubprotocol(c.server?.client).protocol)}
                                                                alt={getProtocolAndSubprotocol(c.server?.client).protocol}
                                                            />
                                                        ) : null}
                                                        <span>
                                                            {getProtocolAndSubprotocol(c.server?.client).protocol}
                                                            {!isVlessProfile
                                                                ? ` ${getProtocolAndSubprotocol(c.server?.client).subprotocol}`
                                                                : ''}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={classes.configActions}>
                                                {!isVlessProfile ? (
                                                <button
                                                    type="button"
                                                    className={classes.iconBtn}
                                                    title="Скачать"
                                                    aria-label="Скачать"
                                                    onClick={() => downloadWebConfig(c.name)}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M12 3V14M12 14L7.5 9.5M12 14L16.5 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M4 15.5V17.5C4 19.1569 5.34315 20.5 7 20.5H17C18.6569 20.5 20 19.1569 20 17.5V15.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                                    </svg>
                                                </button>
                                                ) : null}
                                                {!isVlessProfile && ['Wireguard', 'AmneziaWG'].includes(getProtocolAndSubprotocol(c.server?.client).protocol) ? (
                                                    <button
                                                        type="button"
                                                        className={classes.iconBtn}
                                                        title="QR-код"
                                                        aria-label="QR-код"
                                                        onClick={() => onOpenQr(c)}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M4 4H10V10H4V4ZM14 4H20V10H14V4ZM4 14H10V20H4V14Z" stroke="currentColor" strokeWidth="1.8"/>
                                                            <path d="M14 14H16V16H14V14ZM18 14H20V16H18V14ZM14 18H16V20H14V18ZM16 16H18V18H16V16ZM18 18H20V20H18V18Z" fill="currentColor"/>
                                                        </svg>
                                                    </button>
                                                ) : null}
                                                <button
                                                    type="button"
                                                    className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
                                                    title="Удалить"
                                                    aria-label="Удалить"
                                                    onClick={() => onDelete(c)}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M4 7H20M9 7V5.5C9 4.67157 9.67157 4 10.5 4H13.5C14.3284 4 15 4.67157 15 5.5V7M8 7V18C8 19.1046 8.89543 20 10 20H14C15.1046 20 16 19.1046 16 18V7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                                        <path d="M10.5 10V16M13.5 10V16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })
                            ) : (
                                <div className={classes.empty}>Пока нет VPN-серверов. Нажмите “Добавить VPN-сервер” для WireGuard/AmneziaWG или получите VLESS-подписку выше.</div>
                            )}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {addOpen ? (
                <div
                    className={classes.modalOverlay}
                    onClick={() => setAddOpen(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <div className={classes.modalTitle}>Добавить VPN-сервер</div>
                            <button type="button" className={classes.closeBtn} onClick={() => setAddOpen(false)}>
                                Закрыть
                            </button>
                        </div>

                        <div className={classes.protocolTabs} role="tablist" aria-label="Протокол">
                            {[
                                {id: 'Wireguard', label: 'Wireguard'},
                                {id: 'AmneziaWG', label: 'AmneziaWG'},
                            ].map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={protocol === p.id}
                                    className={`${classes.protocolTab} ${protocol === p.id ? classes.protocolTabActive : ''}`}
                                    onClick={() => setProtocol(p.id)}
                                >
                                    <span className={classes.protocolTabInner}>
                                        {getProtocolIconSrc(p.id) ? (
                                            <img
                                                className={classes.protocolTabIcon}
                                                src={getProtocolIconSrc(p.id)}
                                                alt=""
                                            />
                                        ) : null}
                                        {p.label}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {tunnelConfigs.length === 3 && !isVipUser ? (
                            <div className={classes.proTariffHint}>
                                {String(profile?.tariff_name || '') === 'Legacy'
                                    ? 'Ваш тариф будет переведён на тариф Pro: до 6 серверов, 6₽/сут (180₽/мес). Если у вас снова будет менее 4 серверов, тариф снова вернётся к прежнему'
                                    : 'Ваш тариф будет переведён на тариф Pro: до 6 серверов, 10₽/сут (300₽/мес). Если у вас снова будет менее 4 серверов, тариф снова вернётся к прежнему'}
                            </div>
                        ) : null}

                        {premiumConfirmOpen && premiumConfirmOpt ? (
                            <div
                                className={classes.modalOverlay}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPremiumConfirmOpen(false);
                                }}
                                role="dialog"
                                aria-modal="true"
                            >
                                <div className={classes.premiumModalCard} onClick={(e) => e.stopPropagation()}>
                                    <div className={classes.modalHeader}>
                                        <div className={classes.modalTitle}>
                                            Premium-конфиг: правила и стоимость
                                        </div>
                                        <button
                                            type="button"
                                            className={classes.closeBtn}
                                            onClick={() => setPremiumConfirmOpen(false)}
                                        >
                                            Закрыть
                                        </button>
                                    </div>

                                    <div className={classes.premiumConfirmText}>
                                        <p>
                                            Пожалуйста, подтвердите, что вы понимаете условия Premium-конфигурации:
                                        </p>
                                        <p>
                                            <strong>Лимит на сервер</strong>
                                            <br />
                                            На каждый Premium-сервер мы допускаем не более 3 человек. Это сделано, чтобы снизить шанс перегрузки и блокировки сервера.
                                        </p>
                                        <p>
                                            <strong>Стоимость</strong>
                                            <br />
                                            После создания этого Premium-конфига с вашего баланса будет списываться 33₽ в день (≈ 1000₽ в месяц) за каждый Premium-конфиг.
                                            Если вы удалите его, то тариф станет прежним.
                                        </p>

                                        <p>
                                            <strong>Правила безопасности (очень важно)</strong>
                                            <br />
                                            С этого Premium-конфига крайне не рекомендуется заходить на:
                                        </p>
                                        <ul>
                                            <li>Госуслуги</li>
                                            <li>Max</li>
                                            <li>и любые сервисы, которые просят выключить VPN</li>
                                        </ul>
                                    </div>

                                    <div className={classes.premiumModalActions}>
                                        <button
                                            type="button"
                                            className={classes.btnPrimary}
                                            disabled={creating}
                                            onClick={confirmPremiumCreate}
                                        >
                                            Подтверждаю и создать Premium-конфиг
                                        </button>
                                        <button
                                            type="button"
                                            className={classes.btnDanger}
                                            disabled={creating}
                                            onClick={() => setPremiumConfirmOpen(false)}
                                        >
                                            Отмена
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div className={classes.countryList}>
                            {currentOptions.length ? (
                                currentOptions
                                    .filter((opt) => !isVipUser || !opt.is_premium)
                                    .map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        className={classes.countryBtn}
                                        disabled={creating}
                                        onClick={() => onCreate(opt)}
                                    >
                                        <div className={classes.countryCardRow}>
                                            <div className={classes.countryCardLeft}>
                                                {getCountryIcon(opt.name) ? (
                                                    <img
                                                        className={classes.serverCountryIcon}
                                                        src={getCountryIcon(opt.name)}
                                                        alt=""
                                                    />
                                                ) : null}
                                                <div className={classes.countryNameColumn}>
                                                    <div className={classes.serverCountry}>
                                                        {opt.name}{' '}
                                                        {opt.is_premium ? (
                                                            <span className={classes.premiumSmall}>Premium</span>
                                                        ) : null}
                                                    </div>
                                                    <div className={classes.serverProtocol}>
                                                        {getVlessInbound(opt.client) || opt.client}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))
                            ) : (
                                <div className={classes.emptySmall}>Нет доступных стран для выбранного протокола.</div>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            <TopupModal open={topupOpen} onClose={() => setTopupOpen(false)} onProfileRefresh={refreshProfileOnly} />

            {qrOpen ? (
                <div
                    className={classes.modalOverlay}
                    onClick={() => setQrOpen(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <div className={classes.modalTitle}>QR-код: {qrTitle}</div>
                            <button type="button" className={classes.closeBtn} onClick={() => setQrOpen(false)}>
                                Закрыть
                            </button>
                        </div>
                        <div className={classes.qrModalBody}>
                            <img className={classes.qrImage} src={qrImage} alt="QR-код VPN-сервера" />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default Cabinet;

